// ===== Market Watch panel: search, sortable columns, blinking quotes, context menu, drag to chart =====
import { useState } from "react";
import { useStore, setUI, update, newChart, chartOf, setChart } from "./store";
import { openMenu } from "./ContextMenu";
import { I } from "./Icons";

const COLS = [["name", "Symbol"], ["bid", "Bid"], ["ask", "Ask"], ["spread", "Spread"], ["chg", "Daily Change"]];

export default function MarketWatch() {
  const st = useStore();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [sel, setSel] = useState("EURUSD");
  const now = new Date();

  const rows = st.watch
    .map((n) => st.symbols[n])
    .filter((s) => s.name.toLowerCase().includes(q.toLowerCase()))
    .map((s) => ({ ...s, spreadNow: Math.round((s.ask - s.bid) / s.point), chg: ((s.bid - s.open) / s.open) * 100 }));
  if (sort.key) rows.sort((a, b) => {
    const k = sort.key === "spread" ? "spreadNow" : sort.key;
    return (a[k] > b[k] ? 1 : a[k] < b[k] ? -1 : 0) * sort.dir;
  });

  const rowMenu = (e, name) => {
    setSel(name);
    openMenu(e, [
      { label: "New Order", shortcut: "F9", onClick: () => setUI({ dialog: { kind: "order", symbol: name, mode: "market" } }) },
      { label: "Chart Window", onClick: () => newChart(name, chartOf().tf) },
      { label: "Tick Chart", onClick: () => newChart(name, "M1") },
      { label: "Show in Active Chart", onClick: () => setChart({ symbol: name }) },
      { label: "Depth of Market", shortcut: "Alt+B", onClick: () => setUI({ dom: name }) },
      { label: "Specification", onClick: () => setUI({ dialog: { kind: "spec", symbol: name } }) },
      { sep: true },
      { label: "Hide", shortcut: "Delete", onClick: () => update((s) => { s.watch = s.watch.filter((x) => x !== name); }) },
      { label: "Hide All", onClick: () => update((s) => { s.watch = s.watch.filter((x) => s.positions.some((p) => p.symbol === x)); }) },
      { label: "Show All", onClick: () => update((s) => { s.watch = Object.keys(s.symbols); }) },
      { sep: true },
      { label: "Symbols", shortcut: "Ctrl+U", onClick: () => setUI({ dialog: { kind: "symbols" } }) },
      { label: "Sets", items: [{ label: "Forex", onClick: () => update((s) => { s.watch = Object.keys(s.symbols).filter((x) => s.symbols[x].contract === 1e5); }) }, { label: "Crypto", onClick: () => update((s) => { s.watch = ["BTCUSD", "ETHUSD", "LTCUSD", "XRPUSD", "SOLUSD"]; }) }, { label: "All Symbols", onClick: () => update((s) => { s.watch = Object.keys(s.symbols); }) }] },
      { sep: true },
      { label: "Sort", items: COLS.map(([k, l]) => ({ label: l, onClick: () => setSort({ key: k, dir: 1 }) })) },
    ]);
  };

  return (
    <div className="mt-panel mw" data-testid="market-watch">
      <div className="mt-panel-title">
        <span>Market Watch: {String(now.getHours()).padStart(2, "0")}:{String(now.getMinutes()).padStart(2, "0")}:{String(now.getSeconds()).padStart(2, "0")}</span>
        <button className="mt-panel-close" data-testid="market-watch-close" onClick={() => setUI({ panels: { ...st.panels, market: false } })}><I.close /></button>
      </div>
      <div className="mw-search">
        <input data-testid="market-watch-search" placeholder="Search symbol..." value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="mt-scroll">
        <table className="mt-table mw-table">
          <thead>
            <tr>
              {COLS.map(([k, l]) => (
                <th key={k} data-testid={`mw-sort-${k}`} onClick={() => setSort({ key: k, dir: sort.key === k ? -sort.dir : 1 })} className={k === "name" ? "l" : ""}>
                  {l}{sort.key === k ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr
                key={s.name}
                className={sel === s.name ? "sel" : ""}
                data-testid={`mw-row-${s.name}`}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify({ kind: "symbol", value: s.name }))}
                onClick={() => setSel(s.name)}
                onDoubleClick={() => setUI({ dialog: { kind: "order", symbol: s.name, mode: "market" } })}
                onContextMenu={(e) => rowMenu(e, s.name)}
              >
                <td className="l"><span className="mw-arrow">{s.chg >= 0 ? <I.up /> : <I.down />}</span>{s.name}{(s.live || s.anchored) && <span className="mw-live" title={s.live ? "Live Binance price" : s.closed ? "Real market price (market closed)" : "Real market price"} data-testid={`mw-live-${s.name}`}>●</span>}</td>
                <td className={`blink-${s.dir}`} data-testid={`mw-bid-${s.name}`}>{s.bid.toFixed(s.digits)}</td>
                <td className={`blink-${s.dir}`} data-testid={`mw-ask-${s.name}`}>{s.ask.toFixed(s.digits)}</td>
                <td>{s.spreadNow}</td>
                <td className={s.chg >= 0 ? "pos" : "neg"}>{s.chg.toFixed(2)}%</td>
              </tr>
            ))}
            <tr className="mw-add" onClick={() => setUI({ dialog: { kind: "symbols" } })} data-testid="mw-add-symbol"><td className="l" colSpan={5}>+ click to add...</td></tr>
          </tbody>
        </table>
      </div>
      <div className="mt-panel-tabs">
        <div className="mt-tab active">Symbols</div>
        <div className="mt-tab">Details</div>
        <div className="mt-tab">Trading</div>
        <div className="mt-tab">Ticks</div>
      </div>
    </div>
  );
}
